import { AxiosProgressEvent } from 'axios'
import { useQuery } from '@tanstack/react-query'
import { clientInstance } from '../utils/http-client'
import {
  Document,
  DocumentTag,
  DocumentListResponse,
  DocumentFilters,
  DocumentContainerQuota,
} from '../types/documents'

export interface FileExtensionOption {
  label: string
  value: string
}

export interface UploadDocumentParams {
  file: File
  name?: string
  tags?: string[]
  product_id?: number
  variant_id?: number
  contact_company_id?: number
}

export interface UpdateDocumentParams {
  name?: string
  tags?: string[]
  product_id?: number | null
  variant_id?: number | null
  contact_company_id?: number | null
}

export interface ReplaceDocumentParams {
  file: File
  name?: string
}

/**
 * Upload a document with optional metadata.
 *
 * @param params - Upload parameters
 * @param onProgress - Progress callback (0-100)
 * @returns Promise<Document>
 * @throws Error with validation messages
 */
export const uploadDocument = async (
  params: UploadDocumentParams,
  onProgress?: (progress: number) => void
): Promise<Document> => {
  const formData = new FormData()
  formData.append('file', params.file)

  if (params.name) {
    formData.append('name', params.name)
  }

  if (params.tags && params.tags.length > 0) {
    formData.append('tags', JSON.stringify(params.tags))
  }

  if (params.product_id) {
    formData.append('product_id', params.product_id.toString())
  }

  if (params.variant_id) {
    formData.append('variant_id', params.variant_id.toString())
  }

  if (params.contact_company_id) {
    formData.append('contact_company_id', params.contact_company_id.toString())
  }

  try {
    const response = await clientInstance.post<Document>(
      '/documents/',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent: AxiosProgressEvent) => {
          if (onProgress && progressEvent.total) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            )
            onProgress(percentCompleted)
          }
        },
      }
    )

    return response.data
  } catch (error: any) {
    // Transform API errors to user-friendly messages
    if (error.response?.data) {
      const errors = error.response.data

      if (typeof errors === 'object') {
        // Extract first error message
        const firstError = Object.values(errors)[0]

        // Handle array of errors
        if (Array.isArray(firstError)) {
          const errorItem = firstError[0]
          // Handle ErrorDetail objects from DRF
          if (
            typeof errorItem === 'object' &&
            errorItem !== null &&
            'string' in errorItem &&
            typeof errorItem.string === 'string'
          ) {
            throw new Error(errorItem.string)
          }
          throw new Error(String(errorItem))
        }

        // Handle single error (could be ErrorDetail object or string)
        if (
          typeof firstError === 'object' &&
          firstError !== null &&
          'string' in firstError &&
          typeof firstError.string === 'string'
        ) {
          throw new Error(firstError.string)
        }

        // Handle plain string errors
        if (typeof firstError === 'string') {
          throw new Error(firstError)
        }

        throw new Error(String(firstError))
      }
    }
    throw new Error(error.message || 'Upload failed')
  }
}

/**
 * Get paginated list of documents with filtering, sorting, and pagination.
 *
 * @param filters - Filter, sort, and pagination parameters
 * @param signal - Optional AbortSignal for cancellation
 * @returns Promise<DocumentListResponse>
 */
export const getDocuments = async (
  filters?: DocumentFilters,
  signal?: AbortSignal
): Promise<DocumentListResponse> => {
  const response = await clientInstance.get<DocumentListResponse>(
    '/documents/',
    {
      signal,
      params: filters,
    }
  )
  return response.data
}

/**
 * Get storage quota information for current user's company.
 *
 * @returns Promise<DocumentContainerQuota>
 */
export const getStorageQuota = async (): Promise<DocumentContainerQuota> => {
  const response =
    await clientInstance.get<DocumentContainerQuota>('/documents/quota/')
  return response.data
}

/**
 * Get unique file extensions for documents in current user's company.
 *
 * @returns Promise<FileExtensionOption[]>
 */
export const getFileExtensions = async (): Promise<FileExtensionOption[]> => {
  const response = await clientInstance.get<FileExtensionOption[]>(
    '/documents/file-extensions/'
  )
  return response.data
}

/**
 * Hook to fetch unique file extensions for documents filter.
 *
 * @returns Query result with file extension options
 */
export const useGetFileExtensionOptions = () => {
  return useQuery({
    queryKey: ['documents', 'file-extensions'],
    queryFn: getFileExtensions,
    staleTime: 5 * 60 * 1000, // 5 minutes - extensions don't change frequently
  })
}

/**
 * Search document tags for autocomplete.
 *
 * @param search - Search query
 * @returns Promise<DocumentTag[]>
 */
export const searchDocumentTags = async (
  search: string
): Promise<DocumentTag[]> => {
  const response = await clientInstance.get<DocumentTag[]>('/documents/tags/', {
    params: { search },
  })
  return response.data
}

/**
 * Update a document's metadata.
 *
 * @param documentId - ID of the document to update
 * @param params - Update parameters (only provided fields will be updated)
 * @returns Promise<Document>
 * @throws Error with validation messages
 */
export const updateDocument = async (
  documentId: number,
  params: UpdateDocumentParams
): Promise<Document> => {
  // Build request body with only provided fields
  const data: Record<string, any> = {}

  if (params.name !== undefined) {
    data.name = params.name
  }

  if (params.tags !== undefined) {
    data.tags = JSON.stringify(params.tags)
  }

  if (params.product_id !== undefined) {
    data.product_id = params.product_id
  }

  if (params.variant_id !== undefined) {
    data.variant_id = params.variant_id
  }

  if (params.contact_company_id !== undefined) {
    data.contact_company_id = params.contact_company_id
  }

  try {
    const response = await clientInstance.patch<Document>(
      `/documents/${documentId}/`,
      data
    )
    return response.data
  } catch (error: any) {
    // Transform API errors to user-friendly messages
    if (error.response?.data) {
      const errors = error.response.data

      // Handle DRF detail format
      if (errors.detail) {
        throw new Error(errors.detail)
      }

      if (typeof errors === 'object') {
        // Extract first error message
        const firstError = Object.values(errors)[0]

        // Handle array of errors
        if (Array.isArray(firstError)) {
          const errorItem = firstError[0]
          // Handle ErrorDetail objects from DRF
          if (
            typeof errorItem === 'object' &&
            errorItem !== null &&
            'string' in errorItem &&
            typeof errorItem.string === 'string'
          ) {
            throw new Error(errorItem.string)
          }
          throw new Error(String(errorItem))
        }

        // Handle single error (could be ErrorDetail object or string)
        if (
          typeof firstError === 'object' &&
          firstError !== null &&
          'string' in firstError &&
          typeof firstError.string === 'string'
        ) {
          throw new Error(firstError.string)
        }

        // Handle plain string errors
        if (typeof firstError === 'string') {
          throw new Error(firstError)
        }

        throw new Error(String(firstError))
      }
    }
    throw new Error(error.message || 'Update failed')
  }
}

/**
 * Replace a document with a new file version.
 *
 * @param documentId - ID of the document to replace
 * @param params - Replace parameters
 * @param onProgress - Progress callback (0-100)
 * @returns Promise<Document>
 * @throws Error with validation messages
 */
export const replaceDocument = async (
  documentId: number,
  params: ReplaceDocumentParams,
  onProgress?: (progress: number) => void
): Promise<Document> => {
  const formData = new FormData()
  formData.append('file', params.file)

  if (params.name) {
    formData.append('name', params.name)
  }

  try {
    const response = await clientInstance.post<Document>(
      `/documents/${documentId}/replace/`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent: AxiosProgressEvent) => {
          if (onProgress && progressEvent.total) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            )
            onProgress(percentCompleted)
          }
        },
      }
    )

    return response.data
  } catch (error: any) {
    // Transform API errors to user-friendly messages (same as uploadDocument)
    if (error.response?.data) {
      const errors = error.response.data

      // Handle DRF detail format
      if (errors.detail) {
        throw new Error(errors.detail)
      }

      if (typeof errors === 'object') {
        const firstError = Object.values(errors)[0]
        if (Array.isArray(firstError)) {
          const errorItem = firstError[0]
          if (
            typeof errorItem === 'object' &&
            errorItem !== null &&
            'string' in errorItem &&
            typeof errorItem.string === 'string'
          ) {
            throw new Error(errorItem.string)
          }
          throw new Error(String(errorItem))
        }

        if (
          typeof firstError === 'object' &&
          firstError !== null &&
          'string' in firstError &&
          typeof firstError.string === 'string'
        ) {
          throw new Error(firstError.string)
        }

        if (typeof firstError === 'string') {
          throw new Error(firstError)
        }

        throw new Error(String(firstError))
      }
    }
    throw new Error(error.message || 'Replace failed')
  }
}

/**
 * Delete a document by ID.
 *
 * @param documentId - ID of the document to delete
 * @returns Promise<void>
 * @throws Error if deletion fails
 */
export const deleteDocument = async (documentId: number): Promise<void> => {
  await clientInstance.delete(`/documents/${documentId}/`)
}

// =============================================================================
// Bulk Download Types and Functions
// =============================================================================

export interface BulkDocumentDownloadResult {
  blob: Blob
  contentType: string
  filename: string
}

/** Response when starting an async bulk download (multiple documents) */
export interface AsyncBulkDocumentDownloadResponse {
  request_id: number
  status: 'processing' | 'completed' | 'failed' | 'expired'
  progress_percentage: number
  document_count: number
}

/** Response when checking async bulk download status */
export interface BulkDocumentDownloadStatusResponse {
  request_id: number
  status: 'processing' | 'completed' | 'failed' | 'expired'
  progress_percentage: number
  document_count: number
  download_url?: string
  detail?: string
  expires_at?: string
}

/**
 * Extract filename from Content-Disposition header.
 */
function extractFilenameFromHeader(
  contentDisposition: string | null
): string | null {
  if (!contentDisposition) return null
  const match = contentDisposition.match(/filename="?([^";\n]+)"?/)
  return match ? match[1] : null
}

/**
 * Bulk download documents as a single file or ZIP archive.
 *
 * For single document: Returns BulkDocumentDownloadResult with blob directly.
 * For multiple documents: Returns AsyncBulkDocumentDownloadResponse with request_id for polling.
 *
 * @param documentIds - Array of document IDs to download
 * @param signal - AbortSignal for cancellation
 * @param onProgress - Progress callback (0-100)
 * @returns BulkDocumentDownloadResult for single document or AsyncBulkDocumentDownloadResponse for multiple
 */
export async function bulkDownloadDocuments(
  documentIds: number[],
  signal?: AbortSignal,
  onProgress?: (progress: number) => void
): Promise<BulkDocumentDownloadResult | AsyncBulkDocumentDownloadResponse> {
  const request = {
    include_ids: documentIds,
  }

  // Single document - expect blob response (streaming)
  if (documentIds.length === 1) {
    const response = await clientInstance.post(
      '/documents/bulk-download/',
      request,
      {
        responseType: 'blob',
        signal,
        onDownloadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round(
              (progressEvent.loaded / progressEvent.total) * 100
            )
            onProgress(progress)
          }
        },
      }
    )

    const contentType =
      response.headers['content-type'] || 'application/octet-stream'
    const contentDisposition = response.headers['content-disposition']
    const defaultFilename = `document_${new Date().toISOString().split('T')[0]}`

    return {
      blob: response.data,
      contentType,
      filename:
        extractFilenameFromHeader(contentDisposition) || defaultFilename,
    }
  }

  // Multiple documents - expect JSON response with request_id
  const response = await clientInstance.post(
    '/documents/bulk-download/',
    request,
    {
      signal,
      onDownloadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round(
            (progressEvent.loaded / progressEvent.total) * 100
          )
          onProgress(progress)
        }
      },
    }
  )

  return response.data as AsyncBulkDocumentDownloadResponse
}

/**
 * Check the status of an async bulk document download request.
 *
 * @param requestId - The request ID returned from bulkDownloadDocuments
 * @returns BulkDocumentDownloadStatusResponse with status and download URL if completed
 */
export async function getBulkDocumentDownloadStatus(
  requestId: number
): Promise<BulkDocumentDownloadStatusResponse> {
  const response = await clientInstance.get(
    `/documents/bulk-download/${requestId}/status/`
  )
  return response.data
}
